// Panel copy + fabric-motion keyframes for the scroll-scrubbed hero.
//
// The hero text is GLUED to the cloth: its position is driven by FABRIC_D, the
// measured cumulative vertical displacement of the fabric in fabricfinal.mp4.
// FABRIC_D was extracted by optical-flow analysis of the footage rendered ON
// SCREEN in object-fit:cover space, sampled at the exact video.currentTime the
// scrub displays (normalized cross-correlation of full-sheet vertical luminance
// profiles, sub-pixel peak interpolation, every step correlation q >= 0.8, then
// smoothed + isotonic so holds are perfectly still). Because it is keyed to the
// same currentTime the component reads, text position == displayed cloth
// position by construction. Decode-accurate offscreen re-validation: text tracks
// the cloth within ~15-20px over each ~500px feed and to ~0 at every hang.
// Units: fractions of the VIDEO FRAME height. Index i = time i * FABRIC_DT.
//
// The video's real motion grammar (measured): each colourway's sheet FEEDS in
// fast (~0.5-0.8 frame-heights/s), then HANGS still for reading, then the next
// sheet feeds down in front of it. Feed phases start at t ~= 3.6 / 6.0 / 7.45
// / 10.4 s (these are the wipe.t0 values below).
//
// Per panel:
//   anchorT : video time at which this text sits exactly at viewport centre
//             (the middle of its sheet's hang phase).
//   nip     : y (fraction of frame height) of the line where this sheet's roll
//             meets the cloth during the text's emergence - the static reveal
//             line. Biased slightly low so text can only err BELOW the roll.
//   wipe    : the NEXT sheet's descending hem ({ t0: its feed start, nip: its
//             reveal line + safety bias }). Once that feed starts, the clip
//             line follows the hem down so this text can never float on top of
//             the sheet that is covering it.
// Colourway windows for reference: Calico 0-3.8 | Indigo 4.6-6.2 | Mist
// 6.6-7.8 | Ember 8.2-10.6 | Field 11.0-12.0 (video is 12.00s, 1926x1076).

export const VIDEO_WIDTH = 1926;
export const VIDEO_HEIGHT = 1076;

export const FABRIC_DT = 0.04;
// Cumulative vertical displacement of the fabric SHEET (fraction of frame height),
// built by tracking the sharp horizontal SEAMS between colourways as they feed
// down (the seam is a real, high-contrast point on the cloth, so its motion IS
// the sheet's rigid translation - reliable where the low-texture cloth interior
// is not). Rises through each of the four feeds; flat during the still hangs
// between them. Index i = time i * FABRIC_DT. Driving the copy from this makes
// each headline ride exactly with the cloth beneath it.
// prettier-ignore
export const FABRIC_D = [
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.0005, 0.0024, 0.0056, 0.0094, 0.0123, 0.0156,
  0.0205, 0.0264, 0.031, 0.0338, 0.0372, 0.0418, 0.047, 0.0512, 0.0561, 0.0625,
  0.0694, 0.0746, 0.0782, 0.0833, 0.0899, 0.0959, 0.1004, 0.1043, 0.1101, 0.1171,
  0.1232, 0.1277, 0.1312, 0.1355, 0.1396, 0.1426, 0.1449, 0.1472, 0.1498, 0.1521,
  0.1539, 0.1552, 0.1563, 0.1572, 0.1581, 0.159, 0.1605, 0.1615, 0.1612, 0.16,
  0.1589, 0.1589, 0.1591, 0.1619, 0.1692, 0.1806, 0.1918, 0.2012, 0.2139, 0.2302,
  0.2461, 0.259, 0.2716, 0.2885, 0.3067, 0.3239, 0.3402, 0.3564, 0.372, 0.3881,
  0.407, 0.428, 0.4476, 0.4662, 0.4875, 0.5106, 0.5318, 0.5477, 0.5663, 0.5911,
  0.6199, 0.6455, 0.664, 0.685, 0.7119, 0.7406, 0.763, 0.7792, 0.8013, 0.8298,
  0.8573, 0.8752, 0.8827, 0.8852, 0.8852, 0.8852, 0.8852, 0.8852, 0.8852, 0.8852,
  0.8852, 0.8852, 0.8852, 0.8852, 0.8862, 0.8906, 0.9002, 0.9121, 0.9238, 0.9355,
  0.949, 0.9638, 0.9757, 0.9871, 1.0016, 1.0187, 1.0342, 1.0474, 1.0662, 1.0913,
  1.1152, 1.134, 1.1514, 1.1758, 1.2034, 1.2292, 1.252, 1.2744, 1.3019, 1.3303,
  1.3565, 1.3803, 1.4039, 1.4295, 1.455, 1.4807, 1.5065, 1.5302, 1.5546, 1.5814,
  1.6092, 1.6338, 1.6516, 1.6633, 1.6692, 1.6711, 1.6711, 1.6711, 1.6711, 1.6711,
  1.6711, 1.6711, 1.6711, 1.6711, 1.6711, 1.6711, 1.6711, 1.6711, 1.6749, 1.6859,
  1.7043, 1.7266, 1.7492, 1.7723, 1.7954, 1.8193, 1.8422, 1.8619, 1.8854, 1.9162,
  1.9485, 1.973, 1.9901, 2.0138, 2.0437, 2.0715, 2.0935, 2.1147, 2.1445, 2.1795,
  2.2115, 2.2376, 2.2608, 2.2817, 2.2954, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998,
  2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998,
  2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998,
  2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998, 2.2998,
  2.2998, 2.3004, 2.3063, 2.3229, 2.3435, 2.3639, 2.3767, 2.3946, 2.4176, 2.4462,
  2.4746, 2.5027, 2.5312, 2.5601, 2.5877, 2.6145, 2.6468, 2.6809, 2.7145, 2.7392,
  2.762, 2.7937, 2.831, 2.8658, 2.891, 2.9175, 2.9537, 2.9875, 3.0134, 3.0294,
  3.0406, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463,
  3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463, 3.0463,
  3.0463, 3.0463, 3.0463, 3.0463,
];

export const PANELS = [
  {
    id: "calico",
    eyebrow: "01 · WAREHOUSE",
    headline: "Step inside our warehouse.",
    line: "Millions of yards, floor to ceiling. Take the 360° tour and look around like you're standing in the aisles.",
    text: "charcoal",
    bg: "#E8DCC2",
    anchorT: 0, // centred at load; rides the calico sheet from the first frame
    nip: 0.26,
    wipe: { t0: 1.5, nip: 0.08 }, // indigo seam feeds in from the top and covers it
    cta: { label: "Take the 360° tour", type: "route", to: "/warehouse" },
    cue: true,
  },
  {
    id: "indigo",
    eyebrow: "02 · AMERICAN MADE",
    headline: "American made, and proud of it.",
    line: "Domestic spandex and specialty stretch you won't find in import catalogs.",
    text: "warm",
    bg: "#26365E",
    anchorT: 5.25, // indigo hang (5.02-5.5, curve flat there)
    nip: 0.24,
    wipe: { t0: 5.5, nip: 0.14 }, // mist seam feed start
    cta: { label: "See our fabrics", type: "anchor", to: "fabrics" },
  },
  {
    id: "mist",
    eyebrow: "03 · OUR STORY",
    headline: "Sixty years. One family.",
    line: "Two generations of Cherechians, trading fabric since 1967. Still family-owned, still hands-on.",
    text: "charcoal",
    bg: "#B9BFC6",
    anchorT: 7.3, // mist hang (7.1-7.7, curve flat there)
    nip: 0.28,
    wipe: { t0: 7.7, nip: 0.31 }, // ember seam feed start
    cta: null,
  },
  {
    id: "ember",
    eyebrow: "04 · EXCLUSIVE",
    headline: "The same fabric the big names use.",
    line: "The mills commit their excess runs to us, the very goods that otherwise reach only the largest brands in the business. For everyone else, we are the one place to get them.",
    text: "warm",
    bg: "#B04A2B",
    anchorT: 9.5, // ember hangs long and still (8.7-10.27); centre mid-hang
    nip: 0.28,
    wipe: { t0: 10.27, nip: 0.18 }, // field seam feed start
    cta: null,
  },
  {
    id: "field",
    eyebrow: "05 · CONTACT",
    headline: "Let's talk fabric.",
    line: "We supply small and mid-size manufacturers and resellers, by the roll or by the pound.",
    text: "warm",
    bg: "#5F6A3F",
    anchorT: 11.5, // settles with the field sheet as the video ends (11.1-12)
    nip: 0.28,
    wipe: null,
    cta: { label: "Contact us", type: "anchor", to: "contact" },
  },
];

// Both hero encodes are ALL-INTRA (every frame a keyframe) so scroll-scrubbing
// seeks instantly in either direction. Small enough (4.8MB / 1.6MB) to bundle
// same-origin - no CDN, no cross-origin, no stale-cache surprises. Versioned
// filenames bust any previously cached copy.
export const VIDEO_SRC = "/fabricfinal-intra.mp4";
// 720p all-intra encode for phones/tablets - light to fetch on cellular and
// fast to decode per seek; same footage + duration so FABRIC_D still applies.
export const MOBILE_VIDEO_SRC = "/fabricfinal-mobile-i.mp4";
export const POSTER_SRC = "/fabric-poster.jpg";
